/**
 * Section-agnostic hook exposing an entity's available data window.
 *
 * `useEntityDataRange` is the single source of truth for the `{ earliest, latest }`
 * bounds a report's period control may select within. It fetches those bounds
 * from the server-side `getEntityDataRange` resolver, which derives them from
 * assignment / installation dates (the same bounds the authed `AssetReport` /
 * `SystemReport` views used to compute inline). The resolver returns only the
 * two timestamps, so no underlying assignment data crosses the wire.
 *
 * The hook is a thin typed fetch — it carries no scope-specific client-side date
 * reduction; the backend owns that. Consumers map `range.earliest` / `range.latest`
 * straight onto `ReportWithPeriodControls`' `lowerBound` / `anchorEnd` props.
 *
 * The query document (`GetEntityDataRangeDocument`) is a generated typed-document
 * node imported from `@hydrologiq/gql-experience/graphql` — there is no inline
 * `gql` tag (workspace rule). It is fetched through the "primary" (experience)
 * endpoint, mirroring {@link useReport}. In the public share view the "primary"
 * client is the share-scoped Apollo client, so the same hook resolves the range
 * under the share context without any change.
 *
 * Requirements: 7.12 (authed/public views consume the Available_Range from the
 * resolver via this shared hook rather than computing it inline).
 */
import { useMemo } from "react"
import { GetEntityDataRangeDocument } from "@hydrologiq/gql-experience/graphql"
import type {
  GetEntityDataRangeQuery,
  GetEntityDataRangeQueryVariables,
} from "@hydrologiq/gql-experience/graphql"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { type ReportScopeInput, toReportScope } from "./useReport"

/**
 * The entity's available data window as ISO 8601 strings.
 *
 * `earliest` maps onto `ReportWithPeriodControls`' `lowerBound` (oldest
 * selectable instant); `latest` maps onto `anchorEnd` (newest selectable
 * instant). Either may be absent when the bound is unresolved — `earliest` when
 * the entity has no assignment, `latest` while the entity is still active (open
 * assignment / installation).
 */
export interface EntityDataRange {
  /** ISO 8601 earliest selectable instant → `ReportWithPeriodControls` `lowerBound`. */
  earliest?: string
  /** ISO 8601 latest selectable instant → `ReportWithPeriodControls` `anchorEnd`. */
  latest?: string
}

export interface UseEntityDataRangeResult {
  /** The entity's available data window; empty object while first loading. */
  range: EntityDataRange
  /** True while the query is in flight. */
  loading: boolean
  /** A normalised `Error` if the query failed, otherwise `undefined`. */
  error?: Error
}

/**
 * Fetch an entity's available data window.
 *
 * @param scope    Entity kind the range targets (enum or lowercase string).
 * @param entityId Identifier of the entity (asset / system / installation). The
 *                 query is skipped while this is empty.
 */
export function useEntityDataRange(scope: ReportScopeInput, entityId: string): UseEntityDataRangeResult {
  const variables = useMemo<GetEntityDataRangeQueryVariables>(
    () => ({ scope: toReportScope(scope), entityId }),
    [scope, entityId],
  )

  const { data, loading, error } = useQueryWithEndpoint<
    GetEntityDataRangeQuery,
    GetEntityDataRangeQueryVariables
  >(
    GetEntityDataRangeDocument,
    {
      variables,
      skip: !entityId,
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
    },
    "primary",
  )

  // Map the resolver's nullable earliest/latest onto the optional-string shape
  // consumers feed to lowerBound/anchorEnd. No scope-specific reduction here —
  // the backend owns the range derivation.
  const resolved = data?.getEntityDataRange
  const range: EntityDataRange = {
    earliest: resolved?.earliest ?? undefined,
    latest: resolved?.latest ?? undefined,
  }

  return {
    range,
    loading,
    error: error ? new Error(error.message) : undefined,
  }
}

export default useEntityDataRange
